/**
 * Local Execution / Cloud Sync v1 — the client-side local-hot-path state
 * machine. See docs/superpowers/plans/2026-08-20-local-execution-cloud-sync-v1.md.
 *
 * Narrowly responsible for: the readiness gate, minting `operationId` and
 * capturing `baseRevision`, executing `addBox`/`moveObject`/`rotateObject`/
 * `scaleObject` synchronously against the SAME `commands` instance the rest
 * of the editor uses, holding exactly one provisional operation, submitting
 * it asynchronously, retrying a lost ACK with the same `operationId`,
 * reconciling on mismatch/failure, and recording the four measurements this
 * task asks for. It does not relay commands, does not touch the gated
 * `bridge.execute()` path, and does not implement local-hot undo/redo, an
 * offline queue, or pipelining — all deliberately deferred.
 */

import { serializeScene as defaultSerializeScene } from './serialize-scene.js';

const LOCAL_HOT_COMMANDS = new Set(['addBox', 'moveObject', 'rotateObject', 'scaleObject']);
const RETRY_POLL_MS = 250;

export function isLocalHotCommand(cmd) {
  return LOCAL_HOT_COMMANDS.has(cmd);
}

function createOperationId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return `local:${globalThis.crypto.randomUUID()}`;
  }
  return `local:${Date.now().toString(36)}:${Math.random().toString(36).slice(2)}`;
}

function roundTiming(value) {
  return Math.round(Math.max(0, value) * 1000) / 1000;
}

/**
 * @param {object} options
 * @param {Record<string, Function>} options.commands  the createCommands() instance — never a second one
 * @param {object} options.registry                     current registered scene, for serializeScene
 * @param {object} options.bridge                        the connectBridge() instance (submitLocalCommit/ready/revision/forceReconcile)
 * @param {() => object} options.serializeScene           injected so tests can stub it; defaults to src/serialize-scene.js
 * @param {(metrics: object) => void} [options.onMetrics]  called once per settled operation with the four measurements
 * @param {(cmd: string, result: object) => void} [options.onLocalCommand]  called synchronously right after a
 *   successful local mutation — before serialization/submit — so the caller can reuse its existing post-command UI
 *   refresh (selection helpers, Inspector, status/debug) exactly as it would for a gated bridge.execute() reply.
 *   Fires regardless of what happens afterward (serialize/submit/reconcile): the live scene already changed.
 * @param {number} [options.retryPollMs]                   poll interval while waiting for readiness to return; test-only override
 */
export function createLocalExecution({
  commands,
  registry,
  bridge,
  serializeScene,
  onMetrics = () => {},
  onLocalCommand = () => {},
  retryPollMs = RETRY_POLL_MS,
}) {
  /** At most one outstanding provisional operation, per the MVP constraint. */
  let provisional = null;

  function ready() {
    return Boolean(bridge?.ready);
  }

  function eligible(cmd) {
    return isLocalHotCommand(cmd) && ready() && provisional === null;
  }

  async function waitUntilReady() {
    while (!ready()) {
      await new Promise((resolve) => setTimeout(resolve, retryPollMs));
    }
  }

  async function submitAndSettle(entry) {
    for (;;) {
      if (!ready()) {
        // Connectivity disappeared after local execution: the provisional
        // operation stays exactly as it is (same operationId) and this
        // simply waits for readiness to return before retrying — not a
        // queue, and no second provisional operation can start meanwhile.
        await waitUntilReady();
      }
      let response;
      const submitStartedAt = performance.now();
      try {
        response = await bridge.submitLocalCommit({
          operationId: entry.operationId,
          baseRevision: entry.baseRevision,
          cmd: entry.cmd,
          args: entry.args,
          result: entry.result,
          canonicalScene: entry.canonicalScene,
        });
      } catch {
        // Unknown outcome (timeout or disconnect before an answer arrived).
        // Retry with the SAME operationId once ready again — never
        // re-executes the local command.
        continue;
      }
      entry.metrics.submitToAckMs = roundTiming(performance.now() - submitStartedAt);

      if (response.ok) {
        bridge.noteCommittedRevision(response.revision);
        provisional = null;
        onMetrics({ ...entry.metrics, ok: true, revision: response.revision });
        return;
      }

      // Any definitive negative answer (revision mismatch, or any other
      // rejection) converges on the same primitive: reconcile from
      // cloud-authoritative state, THEN discard the provisional operation —
      // the lock stays held through the reconcile trigger itself, not
      // cleared a moment before it. No merge/replay for v1.
      bridge.forceReconcile();
      provisional = null;
      onMetrics({ ...entry.metrics, ok: false, kind: response.kind ?? 'validation-failed' });
      return;
    }
  }

  /**
   * Execute `cmd` immediately against the live scene and return its visible
   * result — the caller sees this resolve without waiting on the network,
   * exactly like `bridge.execute()`'s resolved value, just without the
   * round trip. Throws synchronously (scene unchanged) if not eligible;
   * callers are expected to check `eligible(cmd)` first and fall back to
   * `bridge.execute()` when it is false.
   */
  function execute(cmd, args = {}) {
    if (!eligible(cmd)) {
      throw new Error(`local execution is not available for '${cmd}' right now`);
    }

    const operationId = createOperationId();
    const baseRevision = bridge.revision;

    const localStartedAt = performance.now();
    const result = commands[cmd](args);
    const localExecutionMs = roundTiming(performance.now() - localStartedAt);

    // The live scene already reflects the final result — refresh the same
    // UI a gated bridge.execute() success would refresh (selection
    // helpers/outline, Inspector, status/debug), regardless of what happens
    // to serialization/submission/reconciliation afterward.
    onLocalCommand(cmd, result);

    // From this point on the local scene has already changed. ANY failure
    // before a successful submit — including a canonicalScene serialization
    // failure below — must be treated as an uncommitted provisional
    // mutation requiring reconciliation, never a silent return to normal
    // operation with an untracked local mutation. The provisional lock is
    // engaged immediately so a second local-hot op cannot start in the
    // window before that failure is handled.
    provisional = { operationId, baseRevision, cmd, args, result };

    let canonicalScene;
    let serializeSceneMs;
    let payloadBytes;
    try {
      const serializeStartedAt = performance.now();
      canonicalScene = registry ? (serializeScene ?? defaultSerializeScene)(registry) : [];
      serializeSceneMs = roundTiming(performance.now() - serializeStartedAt);
      payloadBytes = new TextEncoder().encode(
        JSON.stringify({ operationId, baseRevision, cmd, args, result, canonicalScene }),
      ).length;
    } catch (error) {
      // Never submitted (the transport was never touched) and never folded
      // into any provisional bookkeeping the retry/idempotency path relies
      // on. The only safe outcome is the same one an unreachable/rejected
      // submit gets: discard the provisional annotation and reconcile from
      // cloud-authoritative state. `eligible()`/`bridge.ready` independently
      // stay false for the duration of that reconciliation (forceReconcile
      // sets the bridge's revision to null before reconnecting), so clearing
      // the lock here does not let a second local-hot op slip in ahead of it.
      onMetrics({
        operationId,
        localExecutionMs,
        serializeSceneMs: null,
        payloadBytes: null,
        submitToAckMs: null,
        ok: false,
        kind: 'serialize-failed',
        error: error instanceof Error ? error.message : String(error),
      });
      // Reconcile FIRST, while the lock is still held, then clear it — the
      // lock must not disappear a moment before reconciliation is invoked.
      bridge.forceReconcile();
      provisional = null;
      return result;
    }

    const entry = {
      operationId,
      baseRevision,
      cmd,
      args,
      result,
      canonicalScene,
      metrics: { operationId, localExecutionMs, serializeSceneMs, payloadBytes, submitToAckMs: null },
    };
    void submitAndSettle(entry);

    return result;
  }

  return {
    eligible,
    execute,
    get provisional() { return provisional ? { ...provisional } : null; },
  };
}
